import React, { useEffect, useState, useCallback } from 'react';
import { Award, Download, Share2, Copy, X, Loader2, Twitter } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CourseCertificateModal — Feb 2026.
 *
 * Renders and shares a per-course completion certificate PNG.
 *
 * Trigger:
 *   • Auto-opens after the final-exam pass in LearnCourse.jsx.
 *   • Manually triggerable from a course row / details page.
 *
 * Props:
 *   courseId    — required. e.g. "w1", "r2", "b3"
 *   open        — visibility toggle
 *   onOpenChange(bool) — parent state setter
 *   variant?    — "celebrate" (default headline) or "view"
 */
const CourseCertificateModal = ({ courseId, open, onOpenChange, variant = 'celebrate' }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/learn/courses/${courseId}/certificate/status`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Could not load certificate status');
        setStatus(null);
      } else {
        setStatus(data);
        if (data.passed) {
          setImgUrl(`${BACKEND_URL}${data.certificate_url}?t=${Date.now()}`);
        }
      }
    } catch (_e) {
      toast.error('Network error loading certificate');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (open) fetchStatus();
  }, [open, fetchStatus]);

  const download = async () => {
    if (!status?.certificate_url) return;
    try {
      const res = await authFetch(`${BACKEND_URL}${status.certificate_url}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smarter-learning-${courseId}-certificate.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch (_e) {
      toast.error('Download failed');
    }
  };

  const shareText = status
    ? `I just completed "${status.course_title}" on Smarter Learning Hub — ${status.pct}% on the final exam, +${(status.xp_earned || 0).toLocaleString()} XP earned. 🎓`
    : '';

  const shareOn = (platform) => {
    const url = 'https://smarteryou.live';
    const encText = encodeURIComponent(shareText);
    const encUrl = encodeURIComponent(url);
    let target = null;
    if (platform === 'twitter') target = `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`;
    else if (platform === 'whatsapp') target = `https://wa.me/?text=${encText}%20${encUrl}`;
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText + ' https://smarteryou.live');
      toast.success('Copied — paste anywhere to brag responsibly');
    } catch (_e) {
      toast.error('Could not copy');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-amber-500/30 text-white max-w-2xl"
        data-testid="course-certificate-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Award className="w-7 h-7 text-amber-400" />
            {variant === 'celebrate' ? 'Course complete!' : 'Your certificate'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {status?.passed
              ? `You passed "${status.course_title}" with ${status.pct}%.`
              : 'Pass the final exam (≥ 80%) to unlock your certificate.'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="cert-loading">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        )}

        {!loading && status && !status.passed && (
          <div
            className="rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center space-y-3"
            data-testid="cert-locked"
          >
            <p className="text-lg font-medium text-white">
              Final exam not yet passed
            </p>
            <p className="text-sm text-gray-400">
              Score at least 80% on the final exam to unlock this certificate.
            </p>
          </div>
        )}

        {!loading && status?.passed && imgUrl && (
          <div className="space-y-4">
            <div className="rounded-xl overflow-hidden border border-amber-500/30 shadow-2xl bg-black">
              <img
                src={imgUrl}
                alt={`${status.course_title} completion certificate`}
                className="w-full h-auto block"
                data-testid="cert-image"
              />
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
                <div className="text-xl font-bold text-amber-400">
                  {status.pct}%
                </div>
                <div className="text-xs text-gray-400">Final exam</div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
                <div className="text-xl font-bold text-amber-400">
                  +{(status.xp_earned || 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-400">XP</div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
                <div className="text-xl font-bold text-amber-400">🎓</div>
                <div className="text-xs text-gray-400">Certified</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                onClick={download}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
                data-testid="cert-download-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download PNG
              </Button>
              <Button
                onClick={() => shareOn('twitter')}
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="cert-share-twitter"
              >
                <Twitter className="w-4 h-4 mr-2" /> Share on X
              </Button>
              <Button
                onClick={() => shareOn('whatsapp')}
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="cert-share-whatsapp"
              >
                <Share2 className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <Button
                onClick={copyLink}
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="cert-copy-link"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-white"
            data-testid="cert-close"
          >
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CourseCertificateModal;
